import { FC, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useWindowWidth from '../../../hooks/useWindowWidth'
import { useTranslation } from 'react-i18next'
import { DropdownSelector2 } from '../../../ui'
import { selectDictionariesOptions } from '../../../../store/dictionaries'
import { useAppSelector } from '../../../../store/hooks'
import useRequest from '../../../hooks/useRequest'
import {
    getRegionMapInfo,
    getRegionMapRenewableEnergyTypes,
    getRegionMapAccountedGeneration,
    RegionMapInfo,
    AccountedGenerationInfo,
    getRegionMapOwners,
    RegionOwnerMapInfo,
} from './config'
import TooltipBody from './TooltipBody'
import {
    Point,
    MIN_SCALE,
    MAX_SCALE,
    SCALE_STEP,
    clampOffset,
    computeZoom,
    getViewportCenter,
    wheelDeltaToScale,
} from './helpers'
import style from './style.m.less'
import RussiaRegions from './RussiaRegions'

type HoveredRegion = {
    name: string
    regionId: number | null
    x: number
    y: number
    info: RegionMapInfo | null
    accountedInfo: AccountedGenerationInfo | null
    ownerInfo: RegionOwnerMapInfo | null
    isEmpty: boolean
}

const MapFrame: FC = () => {
    const { t, i18n } = useTranslation()
    const windowWidth = useWindowWidth()
    const isMobile = windowWidth <= 1300
    const isNarrow = windowWidth <= 1000
    const [hoveredRegion, setHoveredRegion] = useState<HoveredRegion | null>(null)
    const [mapMode, setMapMode] = useState(0)
    const [type, setType] = useState(0)
    const [year, setYear] = useState(2026)

    const [allDataByType, setAllDataByType] = useState<Map<number, RegionMapInfo>>(new Map())

    const [availableEnergyTypeIds, setAvailableEnergyTypeIds] = useState<number[] | null>(null)
    const [accountedDataByRegion, setAccountedDataByRegion] = useState<
        Map<number, AccountedGenerationInfo>
    >(new Map())
    const [ownerDataByRegion, setOwnerDataByRegion] = useState<Map<number, RegionOwnerMapInfo>>(
        new Map(),
    )

    const [scale, setScale] = useState(1)
    const [offset, setOffset] = useState<Point>({ x: 0, y: 0 })

    const mapViewportRef = useRef<HTMLDivElement | null>(null)
    const tooltipRef = useRef<HTMLDivElement | null>(null)

    const [isPanning, setIsPanning] = useState(false)
    const panStartRef = useRef<Point | null>(null)
    const offsetStartRef = useRef<Point>({ x: 0, y: 0 })

    const scaleRef = useRef(scale)
    const offsetRef = useRef(offset)

    const { fetch: fetchRegionMapInfo } = useRequest<RegionMapInfo[]>(getRegionMapInfo)
    const { fetch: fetchRenewableEnergyTypes } = useRequest<number[]>(
        getRegionMapRenewableEnergyTypes,
    )
    const { fetch: fetchAccountedGeneration } = useRequest<AccountedGenerationInfo[]>(
        getRegionMapAccountedGeneration,
    )
    const { fetch: fetchOwners } = useRequest<RegionOwnerMapInfo[]>(getRegionMapOwners)

    const { renewableEnergyTypesOptions, regionsOptions } =
        useAppSelector(selectDictionariesOptions) ?? {}

    const locale = useMemo(() => {
        const lang = i18n.language.toLowerCase()
        return lang.includes('ru') ? 'ru' : 'en'
    }, [i18n.language])

    const regionNameToId = useMemo(() => {
        const map = new Map<string, number>()
        if (regionsOptions) {
            for (const r of regionsOptions) {
                map.set(r.text, r.id)
            }
        }
        return map
    }, [regionsOptions])

    const regionIdToTexts = useMemo(() => {
        const map = new Map<number, { ru: string; en: string }>()
        if (regionsOptions) {
            for (const r of regionsOptions) {
                map.set(r.id, {
                    ru: (r as any).texts?.ru ?? r.text,
                    en: (r as any).texts?.en ?? r.text,
                })
            }
        }
        return map
    }, [regionsOptions])

    const mapModeOptions = [
        {
            id: 0,
            value: 0,
            text: t('main.filterMapLocation'),
            label: t('main.filterMapLocation'),
        },
        {
            id: 1,
            value: 1,
            text: t('main.filterMapAccountedGeneration'),
            label: t('main.filterMapAccountedGeneration'),
        },
        {
            id: 2,
            value: 2,
            text: t('main.filterMapOwners'),
            label: t('main.filterMapOwners'),
        },
    ]

    const yearOptions = [
        { id: 2024, value: 2024, text: '2024', label: '2024' },
        { id: 2025, value: 2025, text: '2025', label: '2025' },
        { id: 2026, value: 2026, text: '2026', label: '2026' },
    ]

    const realRenewableEnergyTypesOptions = useMemo(() => {
        const base = [
            {
                id: 0,
                value: 0,
                text: t('main.allRenewableEnergy'),
                label: t('main.allRenewableEnergy'),
            },
        ]
        if (!renewableEnergyTypesOptions || !availableEnergyTypeIds) {
            return base
        }
        const availableSet = new Set(availableEnergyTypeIds)
        const filtered = renewableEnergyTypesOptions.filter((opt: any) => availableSet.has(opt.id))
        return [...base, ...filtered]
    }, [renewableEnergyTypesOptions, availableEnergyTypeIds, t])

    useEffect(() => {
        fetchRenewableEnergyTypes({
            getParams: { map_mode: mapMode },
            showError: false,
            onSuccess: (data) => {
                setAvailableEnergyTypeIds(data)
            },
        })
    }, [mapMode])

    useEffect(() => {
        if (mapMode === 0) {
            return
        }
        if (mapMode === 1) {
            fetchAccountedGeneration({
                getParams: { year },
                showError: false,
                onSuccess: (data) => {
                    const map = new Map<number, AccountedGenerationInfo>()
                    for (const item of data) {
                        map.set(item.region_id, item)
                    }
                    setAccountedDataByRegion(map)
                },
            })
        } else if (mapMode === 2) {
            fetchOwners({
                showError: false,
                onSuccess: (data) => {
                    const map = new Map<number, RegionOwnerMapInfo>()
                    for (const item of data) {
                        map.set(item.region_id, item)
                    }
                    setOwnerDataByRegion(map)
                },
            })
        }
    }, [mapMode, year])

    useEffect(() => {
        const getParams: Record<string, string | number> = {}
        if (type !== 0) {
            getParams.renewable_energy_type_id = type
        }
        fetchRegionMapInfo({
            getParams,
            showError: false,
            onSuccess: (data) => {
                const map = new Map<number, RegionMapInfo>()
                for (const item of data) {
                    map.set(item.region_id, item)
                }
                setAllDataByType(map)
            },
        })
    }, [type])

    useEffect(() => {
        const timer = setTimeout(() => {
            const container = document.querySelector(`.${style.mapSvg}`)
            if (!container) {
                return
            }

            const paths = container.querySelectorAll(
                'path[data-title]',
            ) as NodeListOf<SVGPathElement>
            paths.forEach((path) => {
                const title = path.dataset.title
                if (!title) {
                    return
                }

                const regionId = regionNameToId.get(title)
                if (regionId === undefined) {
                    return
                }

                let isEmpty = true
                if (mapMode === 0) {
                    const info = allDataByType.get(regionId)
                    isEmpty = !info || info.qualified_count === 0
                } else if (mapMode === 1) {
                    const accountedInfo = accountedDataByRegion.get(regionId)
                    isEmpty = !accountedInfo || accountedInfo.total_volume === 0
                } else if (mapMode === 2) {
                    const ownerInfo = ownerDataByRegion.get(regionId)
                    if (!ownerInfo || !ownerInfo.energy_types) {
                        isEmpty = true
                    } else if (type === 0) {
                        isEmpty = !ownerInfo.energy_types.some(
                            (et) => et.owners && et.owners.length > 0,
                        )
                    } else {
                        const specificType = ownerInfo.energy_types.find(
                            (et) => et.energy_type_id === type,
                        )
                        isEmpty =
                            !specificType ||
                            !specificType.owners ||
                            specificType.owners.length === 0
                    }
                }

                if (isEmpty) {
                    path.style.fill = 'rgb(189, 217, 206)'
                    path.style.color = 'rgb(189, 217, 206)'
                } else {
                    path.style.fill = ''
                    path.style.color = ''
                }
            })
        }, 50)

        return () => clearTimeout(timer)
    }, [allDataByType, accountedDataByRegion, ownerDataByRegion, regionNameToId, mapMode, type])

    const onMouseMove = useCallback(
        (event: MouseEvent<HTMLDivElement>) => {
            if (isMobile) return

            const target = event.target as Element
            const path = target.closest('path[data-title]') as SVGPathElement | null

            if (!path) {
                setHoveredRegion(null)
                return
            }

            const title = path.dataset.title
            if (!title) {
                setHoveredRegion(null)
                return
            }

            const x = event.clientX + 16
            const y = event.clientY - 18

            if (hoveredRegion?.name === title) {
                if (tooltipRef.current) {
                    tooltipRef.current.style.left = `${x}px`
                    tooltipRef.current.style.top = `${y}px`
                }
                return
            }

            const regionId = regionNameToId.get(title)
            if (regionId === undefined) {
                setHoveredRegion({
                    name: title,
                    regionId: null,
                    x,
                    y,
                    info: null,
                    accountedInfo: null,
                    ownerInfo: null,
                    isEmpty: false,
                })
                return
            }

            const info = allDataByType.get(regionId) ?? null
            const accountedInfo = accountedDataByRegion.get(regionId) ?? null
            const ownerInfo = ownerDataByRegion.get(regionId) ?? null

            let isEmpty = true
            if (mapMode === 0) {
                isEmpty = !info || info.qualified_count === 0
            } else if (mapMode === 1) {
                isEmpty = !accountedInfo || accountedInfo.total_volume === 0
            } else if (mapMode === 2) {
                if (!ownerInfo || !ownerInfo.energy_types) {
                    isEmpty = true
                } else if (type === 0) {
                    isEmpty = !ownerInfo.energy_types.some(
                        (et) => et.owners && et.owners.length > 0,
                    )
                } else {
                    const specificType = ownerInfo.energy_types.find(
                        (et) => et.energy_type_id === type,
                    )
                    isEmpty =
                        !specificType || !specificType.owners || specificType.owners.length === 0
                }
            }
            setHoveredRegion({
                name: title,
                regionId,
                x,
                y,
                info,
                accountedInfo,
                ownerInfo,
                isEmpty,
            })
        },
        [
            hoveredRegion?.name,
            regionNameToId,
            allDataByType,
            accountedDataByRegion,
            ownerDataByRegion,
            mapMode,
            type,
            isMobile,
        ],
    )

    const onMouseLeave = useCallback(() => {
        if (isMobile) return
        setHoveredRegion(null)
    }, [isMobile])

    const onRegionClick = useCallback(
        (event: MouseEvent<HTMLDivElement>) => {
            if (!isMobile) return

            const target = event.target as Element
            const path = target.closest('path[data-title]') as SVGPathElement | null

            if (!path) {
                setHoveredRegion(null)
                return
            }

            const title = path.dataset.title
            if (!title) {
                setHoveredRegion(null)
                return
            }

            const regionId = regionNameToId.get(title)
            const info = regionId !== undefined ? (allDataByType.get(regionId) ?? null) : null
            const accountedInfo =
                regionId !== undefined ? (accountedDataByRegion.get(regionId) ?? null) : null
            const ownerInfo =
                regionId !== undefined ? (ownerDataByRegion.get(regionId) ?? null) : null

            let isEmpty = true
            if (regionId !== undefined) {
                if (mapMode === 0) {
                    isEmpty = !info || info.qualified_count === 0
                } else if (mapMode === 1) {
                    isEmpty = !accountedInfo || accountedInfo.total_volume === 0
                } else if (mapMode === 2) {
                    if (!ownerInfo || !ownerInfo.energy_types) {
                        isEmpty = true
                    } else if (type === 0) {
                        isEmpty = !ownerInfo.energy_types.some(
                            (et) => et.owners && et.owners.length > 0,
                        )
                    } else {
                        const specificType = ownerInfo.energy_types.find(
                            (et) => et.energy_type_id === type,
                        )
                        isEmpty =
                            !specificType ||
                            !specificType.owners ||
                            specificType.owners.length === 0
                    }
                }
            }

            setHoveredRegion({
                name: title,
                regionId: regionId ?? null,
                x: 0,
                y: 0,
                info,
                accountedInfo,
                ownerInfo,
                isEmpty,
            })
        },
        [
            isMobile,
            regionNameToId,
            allDataByType,
            accountedDataByRegion,
            ownerDataByRegion,
            mapMode,
            type,
        ],
    )

    useEffect(() => {
        scaleRef.current = scale
    }, [scale])

    useEffect(() => {
        offsetRef.current = offset
    }, [offset])

    const hasTransform = scale !== 1

    const zoomToPoint = useCallback((nextScale: number, clientPoint?: Point) => {
        setScale((prevScale) => {
            const result = computeZoom(
                nextScale,
                prevScale,
                offsetRef.current,
                mapViewportRef.current,
                clientPoint,
            )
            scaleRef.current = result.scale
            setOffset(result.offset)
            offsetRef.current = result.offset
            return result.scale
        })
    }, [])

    const zoomToViewportCenter = useCallback(
        (nextScale: number) => {
            const el = mapViewportRef.current
            if (!el) {
                zoomToPoint(nextScale)
                return
            }
            zoomToPoint(nextScale, getViewportCenter(el))
        },
        [zoomToPoint],
    )

    const onZoomIn = useCallback(() => {
        zoomToViewportCenter(scaleRef.current + SCALE_STEP)
    }, [zoomToViewportCenter])

    const onZoomOut = useCallback(() => {
        zoomToViewportCenter(scaleRef.current - SCALE_STEP)
    }, [zoomToViewportCenter])

    const onResetZoom = useCallback(() => {
        scaleRef.current = 1
        offsetRef.current = { x: 0, y: 0 }
        setScale(1)
        setOffset({ x: 0, y: 0 })
        setIsPanning(false)
        panStartRef.current = null
        offsetStartRef.current = { x: 0, y: 0 }
    }, [])

    const wheelScaleHandlerRef = useRef<((e: WheelEvent) => void) | null>(null)

    wheelScaleHandlerRef.current = (event: WheelEvent) => {
        event.preventDefault()
        event.stopPropagation()

        const currentScale = scaleRef.current
        const clampedNext = wheelDeltaToScale(event, currentScale)

        const el = mapViewportRef.current
        if (!el) {
            return
        }

        const clientPoint: Point = { x: event.clientX, y: event.clientY }

        const result = computeZoom(clampedNext, currentScale, offsetRef.current, el, clientPoint)
        setScale(result.scale)
        scaleRef.current = result.scale
        setOffset(result.offset)
        offsetRef.current = result.offset
    }

    useEffect(() => {
        const el = mapViewportRef.current
        if (!el) {
            return
        }

        const handler = (e: WheelEvent) => {
            wheelScaleHandlerRef.current?.(e)
        }
        el.addEventListener('wheel', handler, { passive: false })

        return () => {
            el.removeEventListener('wheel', handler)
        }
    }, [])

    const onPointerDown = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            if (event.button !== 0) {
                return
            }

            const target = event.target as HTMLElement
            if (
                target.closest(`.${style.zoomControls}`) ||
                target.closest(`.${style.resetControls}`)
            ) {
                return
            }

            setIsPanning(true)
            setHoveredRegion(null)
            panStartRef.current = { x: event.clientX, y: event.clientY }
            offsetStartRef.current = offset

            try {
                event.currentTarget.setPointerCapture(event.pointerId)
            } catch {
                // ignore
            }
        },
        [offset],
    )

    const onPointerMove = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            if (!isPanning || !panStartRef.current) {
                return
            }
            event.preventDefault()

            const dx = event.clientX - panStartRef.current.x
            const dy = event.clientY - panStartRef.current.y

            const next = clampOffset(
                {
                    x: offsetStartRef.current.x + dx,
                    y: offsetStartRef.current.y + dy,
                },
                scaleRef.current,
                mapViewportRef.current,
            )
            setOffset(next)
        },
        [isPanning],
    )

    const endPan = useCallback(() => {
        setIsPanning(false)
        panStartRef.current = null
    }, [])

    return (
        <div className={style.mapContainer}>
            <div className={style.headerRow}>
                <DropdownSelector2
                    customStyle={{ width: isMobile ? '100%' : '223px' }}
                    options={mapModeOptions}
                    onChange={(value) => {
                        setMapMode(value)
                        setType(0)
                        setHoveredRegion(null)
                    }}
                    currentLang={locale}
                    value={mapMode}
                />
                {(mapMode === 0 || mapMode === 2) && (
                    <DropdownSelector2
                        customStyle={{ width: '223px' }}
                        options={realRenewableEnergyTypesOptions}
                        onChange={(value) => {
                            setType(value)
                            setHoveredRegion(null)
                        }}
                        currentLang={locale}
                        value={type}
                    />
                )}
                {mapMode === 1 && (
                    <DropdownSelector2
                        customStyle={{ width: '223px' }}
                        options={yearOptions}
                        onChange={(value) => {
                            setYear(value)
                        }}
                        currentLang={locale}
                        value={year}
                    />
                )}
            </div>

            <div
                ref={mapViewportRef}
                className={style.mapViewport}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={endPan}
                onPointerCancel={endPan}
                onPointerLeave={(e) => {
                    endPan()
                    onMouseLeave()
                }}
                onMouseMove={!isMobile ? onMouseMove : undefined}
                onMouseLeave={!isMobile ? onMouseLeave : undefined}
                onClick={isMobile ? onRegionClick : undefined}
                data-panning={isPanning ? 'true' : 'false'}
            >
                <div className={style.zoomControls}>
                    <button
                        type="button"
                        className={style.zoomButton}
                        onClick={onZoomIn}
                        aria-label={t('main.mapZoomIn', { defaultValue: 'Zoom in' })}
                        title={t('main.mapZoomIn', { defaultValue: 'Zoom in' })}
                        disabled={scale >= MAX_SCALE}
                    >
                        +
                    </button>
                    <button
                        type="button"
                        className={style.zoomButton}
                        onClick={onZoomOut}
                        aria-label={t('main.mapZoomOut', { defaultValue: 'Zoom out' })}
                        title={t('main.mapZoomOut', { defaultValue: 'Zoom out' })}
                        disabled={scale <= MIN_SCALE}
                    >
                        −
                    </button>
                </div>

                {hasTransform && (
                    <div className={style.resetControls}>
                        <button
                            type="button"
                            className={style.resetButton}
                            onClick={onResetZoom}
                            aria-label={t('main.mapResetZoom', { defaultValue: 'Reset zoom' })}
                            title={t('main.mapResetZoom', { defaultValue: 'Reset zoom' })}
                        >
                            {t('main.mapResetZoom', { defaultValue: 'Reset zoom' })}
                        </button>
                    </div>
                )}

                <div
                    className={style.mapSvg}
                    onMouseMove={!isMobile ? onMouseMove : undefined}
                    onMouseLeave={!isMobile ? onMouseLeave : undefined}
                >
                    <RussiaRegions translateX={offset.x} translateY={offset.y} scale={scale} />
                </div>
            </div>

            {hoveredRegion && !isMobile && (
                <div
                    ref={tooltipRef}
                    className={style.tooltip}
                    style={{ left: hoveredRegion.x, top: hoveredRegion.y }}
                >
                    <div className={style.tooltipTitle}>
                        {hoveredRegion.regionId !== null
                            ? regionIdToTexts.get(hoveredRegion.regionId)?.[locale] ??
                              hoveredRegion.name
                            : hoveredRegion.name}
                    </div>
                    <TooltipBody
                        mapMode={mapMode}
                        locale={locale}
                        regionInfo={hoveredRegion.info}
                        accountedInfo={hoveredRegion.accountedInfo}
                        ownerInfo={hoveredRegion.ownerInfo}
                        type={type}
                        isEmpty={hoveredRegion.isEmpty}
                        energyTypesOptions={renewableEnergyTypesOptions}
                    />
                </div>
            )}

            {hoveredRegion && isMobile && (
                <div className={style.tooltipModal}>
                    <button
                        type="button"
                        className={style.tooltipCloseBtn}
                        onClick={() => setHoveredRegion(null)}
                        aria-label={t('main.mapCloseTooltip', { defaultValue: 'Close' })}
                    >
                        ×
                    </button>
                    <div className={style.tooltipTitle}>
                        {hoveredRegion.regionId !== null
                            ? regionIdToTexts.get(hoveredRegion.regionId)?.[locale] ??
                              hoveredRegion.name
                            : hoveredRegion.name}
                    </div>
                    <TooltipBody
                        mapMode={mapMode}
                        locale={locale}
                        regionInfo={hoveredRegion.info}
                        accountedInfo={hoveredRegion.accountedInfo}
                        ownerInfo={hoveredRegion.ownerInfo}
                        type={type}
                        isEmpty={hoveredRegion.isEmpty}
                        energyTypesOptions={renewableEnergyTypesOptions}
                    />
                </div>
            )}
        </div>
    )
}

export default MapFrame
